import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Mail, Phone, StickyNote } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtPrice } from "@/lib/designOptions";

const TYPE_META = {
  fitting: { label: "Fitting", cls: "border-[#C59B27]/50 text-[#C59B27]" },
  design: { label: "Design Studio", cls: "border-sky-500/40 text-sky-400" },
  wedding: { label: "Wedding", cls: "border-rose-400/40 text-rose-300" },
};
const STATUS_CLS = {
  pending: "border-[#C59B27]/50 text-[#C59B27]",
  confirmed: "border-emerald-500/40 text-emerald-400",
  completed: "border-stone-500/40 text-stone-400",
  cancelled: "border-[#8B2626]/50 text-[#E8A0A0]",
  new: "border-sky-500/40 text-sky-400",
  contacted: "border-[#C59B27]/50 text-[#C59B27]",
  "in-production": "border-emerald-500/40 text-emerald-400",
  closed: "border-stone-500/40 text-stone-400",
};
const STATUS_ENDPOINT = {
  fitting: (id) => `/admin/bookings/${id}`,
  design: (id) => `/admin/design-requests/${id}`,
  wedding: (id) => `/admin/wedding-inquiries/${id}`,
};

const nextActions = (l) => {
  if (l.type === "fitting") {
    if (l.status === "pending") return [["Confirm", "confirmed"], ["Cancel", "cancelled"]];
    if (l.status === "confirmed") return [["Complete", "completed"], ["Cancel", "cancelled"]];
    return [];
  }
  if (l.type === "design") {
    if (l.status === "new") return [["Contacted", "contacted"], ["Close", "closed"]];
    if (l.status === "contacted") return [["In Production", "in-production"], ["Close", "closed"]];
    if (l.status === "in-production") return [["Close", "closed"]];
    return [];
  }
  if (l.status === "new") return [["Contacted", "contacted"], ["Close", "closed"]];
  if (l.status === "contacted") return [["Close", "closed"]];
  return [];
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [notesOpen, setNotesOpen] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data.leads);
      setStats(data.stats);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter(
      (l) =>
        (typeFilter === "all" || l.type === typeFilter) &&
        (!q || [l.name, l.email, l.phone, l.detail, l.source_label, l.lead_id].join(" ").toLowerCase().includes(q))
    );
  }, [leads, search, typeFilter]);

  const setStatus = async (l, status) => {
    try {
      await api.patch(STATUS_ENDPOINT[l.type](l.lead_id), { status });
      toast.success(`${l.name} — marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openNotes = (l) => {
    setNotesOpen(notesOpen === l.lead_id ? null : l.lead_id);
    setNoteDraft(l.notes || "");
  };

  const saveNotes = async (l) => {
    try {
      await api.patch(`/admin/leads/${l.type}/${l.lead_id}/notes`, { notes: noteDraft });
      toast.success("Note saved.");
      setNotesOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = () => {
    const cols = ["Reference", "Name", "Email", "Phone", "Type", "Detail", "Estimate", "Status", "Heard via", "When", "Created", "Notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((l) =>
      [l.lead_id, l.name, l.email, l.phone, TYPE_META[l.type].label, l.detail, l.value || "", l.status, l.source_label, l.when, (l.created_at || "").slice(0, 10), l.notes].map(esc).join(",")
    );
    const blob = new Blob([[cols.map(esc).join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bespoke-maestro-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mt-10" data-testid="admin-leads-tab">
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-leads-stats">
          {[
            ["Total Leads", stats.total],
            ["New This Week", stats.new_this_week],
            ["Open", stats.open],
            ["Design Pipeline (est.)", fmtPrice(stats.pipeline_value) || "$0"],
          ].map(([label, value]) => (
            <div key={label} className="border border-[#2A2825] bg-[#1A1917] p-6" data-testid={`admin-leads-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</p>
              <p className="mt-2 font-serif text-3xl font-light text-[#FFFFFF]">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input
          data-testid="admin-leads-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, phone, cloth…"
          className="min-w-[240px] flex-1 border border-[#2A2825] bg-[#121110] px-4 py-3 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]"
        />
        {[
          ["all", `All (${stats?.total ?? 0})`],
          ["fitting", `Fittings (${stats?.fittings ?? 0})`],
          ["design", `Design Studio (${stats?.designs ?? 0})`],
          ["wedding", `Weddings (${stats?.weddings ?? 0})`],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-leads-filter-${key}`}
            onClick={() => setTypeFilter(key)}
            className={`border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              typeFilter === key ? "border-[#C59B27] text-[#C59B27]" : "border-[#2A2825] text-[#858078] hover:text-[#FFFFFF]"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          data-testid="admin-leads-export-csv"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 bg-[#C59B27] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#121110] transition-colors hover:bg-[#FFFFFF]"
        >
          <Download size={12} /> CSV
        </button>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-leads-list">
        {loading && <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>}
        {!loading && filtered.length === 0 && (
          <p className="border border-[#2A2825] bg-[#1A1917] py-14 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]" data-testid="admin-leads-empty">
            No leads in this view
          </p>
        )}
        {!loading &&
          filtered.map((l) => (
            <div key={`${l.type}-${l.lead_id}`} data-testid={`admin-lead-${l.lead_id}`} className="border border-[#2A2825] bg-[#1A1917] p-6">
              <div className="grid gap-4 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2">
                    <p className="font-serif text-lg text-[#FFFFFF]">{l.name}</p>
                    <span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${TYPE_META[l.type].cls}`}>
                      {TYPE_META[l.type].label}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#C59B27]">{l.lead_id}</p>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-[#858078]">
                    <a href={`mailto:${l.email}`} data-testid={`admin-lead-email-${l.lead_id}`} className="inline-flex items-center gap-1 hover:text-[#C59B27]">
                      <Mail size={11} /> {l.email}
                    </a>
                    <a href={`tel:${l.phone}`} data-testid={`admin-lead-phone-${l.lead_id}`} className="inline-flex items-center gap-1 hover:text-[#C59B27]">
                      <Phone size={11} /> {l.phone}
                    </a>
                  </div>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-sm text-[#FFFFFF]">{l.detail}</p>
                  <p className="mt-1 text-xs text-[#858078]">
                    {l.value ? `Est. from ${fmtPrice(l.value)} · ` : ""}
                    {l.when ? `${l.when} · ` : ""}
                    {(l.created_at || "").slice(0, 10)}
                  </p>
                  <p className="mt-1 text-xs text-[#858078]">
                    Heard via: <span className="text-[#C59B27]" data-testid={`admin-lead-source-${l.lead_id}`}>{l.source_label}</span>
                  </p>
                </div>
                <div className="lg:col-span-2">
                  <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_CLS[l.status]}`}>
                    {l.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                  {nextActions(l).map(([label, status]) => (
                    <button
                      key={status}
                      data-testid={`admin-lead-action-${status}-${l.lead_id}`}
                      onClick={() => setStatus(l, status)}
                      className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors active:scale-95 ${
                        status === "cancelled" || status === "closed"
                          ? "border-[#8B2626]/60 text-[#E8A0A0] hover:bg-[#8B2626] hover:text-white"
                          : "border-[#C59B27] text-[#C59B27] hover:bg-[#C59B27] hover:text-[#121110]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    data-testid={`admin-lead-notes-toggle-${l.lead_id}`}
                    onClick={() => openNotes(l)}
                    className={`inline-flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                      l.notes ? "border-[#C59B27]/60 text-[#C59B27]" : "border-[#2A2825] text-[#858078] hover:text-[#FFFFFF]"
                    }`}
                  >
                    <StickyNote size={11} /> {l.notes ? "Note" : "Add Note"}
                  </button>
                </div>
              </div>
              {notesOpen === l.lead_id ? (
                <div className="mt-4 border-t border-[#2A2825] pt-4">
                  <textarea
                    data-testid={`admin-lead-notes-input-${l.lead_id}`}
                    rows={2}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Private note — prefers navy, wedding in October…"
                    className="w-full border border-[#2A2825] bg-[#121110] px-4 py-3 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]"
                  />
                  <button
                    data-testid={`admin-lead-notes-save-${l.lead_id}`}
                    onClick={() => saveNotes(l)}
                    className="mt-2 border border-[#C59B27] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C59B27] transition-colors hover:bg-[#C59B27] hover:text-[#121110]"
                  >
                    Save Note
                  </button>
                </div>
              ) : (
                l.notes && <p className="mt-3 border-t border-[#2A2825] pt-3 text-xs italic text-[#858078]">"{l.notes}"</p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
